import { UrlLoad } from "@pdfking/compatibility"
import type { AnyError } from "@pdfking/errors"
import { ResultResolve } from "@pdfking/errors"
import type { StandardResponse } from "@pdfking/io"
import { ResponseCreate, StreamCreate, StreamToIterable } from "@pdfking/io"
import type { Executor, TrackingStatus } from "@pdfking/planner"
import { PDFMergeIntegrity } from "@pdfking/renderer-default"
import type { Bytes } from "@pdfking/structs"
import type { ExecutionContext } from "@pdfking/taskmaker"
import { FullMethodDispatcher, RecordAssign, SafePromise, Void, Partial } from "@pdfking/tfunc"

import type { PdfEngineOptions } from "#public"
import type { RemoteFile } from "#public"
import type { VirtualFile } from "#public"
import type { TasksType } from "#tasks"

import { EngineError } from "../../internal/errors/EngineError"
import type { PlannedFile } from "../methods/FilePlan"
import { FilePlan } from "../methods/FilePlan"
import { ProgressFetch } from "../network/ProgressFetch"
import { ErrorName } from "../types/errors/ErrorName"
import { VirtualFileType } from "../types/files/VirtualFileType"
import { TrackedTaskName } from "../types/tasks/TrackedTaskName"

type Emit = (task: TrackingStatus) => void

// tasks re-executed with cache_evict receive it on their TaskContext, and
// their nested RequestFile calls must forward it into the plans they execute
type Engine = Executor<TasksType, ExecutionContext> & { context?: ExecutionContext }

const CacheExpiredQ = (error: AnyError): boolean => error.enum === ErrorName.CACHE_EXPIRED

/** The first mirror that answers, watched while it does.
 *
 * Every url the same way: `fs:` into this engine's own file cache reads
 * without a network at all, `https:` reads across one, and the loader is what
 * knows the difference — so a mirror list can mix them and this does not care
 * which one served. Nothing answering is the caller's to interpret, so it is
 * an absence here rather than an error. */
const try_mirrors = async (file: RemoteFile, emit: Emit): Promise<StandardResponse | undefined> => {
  for (const url of file.mirrors) {
    // Only a read that could take a while is worth watching: a file that
    // carries its own `info` was written by us and is being read back, and
    // announcing a download for what is usually a store hit would put progress
    // on screen for every page of every document.
    const { value: response, error } = await SafePromise(
      file.info
        ? UrlLoad(url)
        : ProgressFetch(url, {
            download: e => emit({ name: TrackedTaskName.DOWNLOAD, ...e }),
          })
    )

    if (response?.ok) return response

    // Every mirror that didn't work, with the reason: the error thrown when
    // they all fail can't distinguish a network/protocol failure from a 404,
    // and this runs in the engine worker where nothing else surfaces it.
    console.warn(
      `pdfengine: mirror failed ${url}`,
      error ?? `HTTP ${response?.status} ${response?.statusText}`,
      response?.info.type
    )
  }

  return undefined
}

/** The plan route: build the file's plan, run it — forwarding cache_evict —
 * and serve what it made.
 *
 * A plan replayed from cache can hand back a file whose bytes have since left
 * the file cache (CACHE_EXPIRED). The answer is one more run with cache_evict,
 * so every task in the plan drops its stale response and re-executes — and
 * because a task names its output after the file it makes (see
 * FileContentGUID), the re-run writes the bytes back under the very names the
 * stale responses hold: nothing here repairs anything, re-running IS the
 * repair. Other errors — and CACHE_EXPIRED when the run was already evicting —
 * propagate. */
const produced =
  (file: PlannedFile, engine: Engine, options: PdfEngineOptions, emit: Emit) =>
  async (): Promise<StandardResponse> => {
    const evicting = engine.context?.cache_evict ?? false

    // a merge or a zip yields its bytes, anything else what holds them
    const once = async (cache_evict: boolean): Promise<StandardResponse> => {
      const made = await engine.execute(FilePlan(file, engine, options), { cache_evict })
      return dispatch("content" in made ? made.content : made, engine, options, emit)
    }

    return ResultResolve<StandardResponse | Promise<StandardResponse>, AnyError>(
      await SafePromise(once(evicting)),
      error => {
        if (evicting || !CacheExpiredQ(error)) throw error
        return once(true)
      }
    )
  }

// The primary way at the bytes, then the fallback when there is one: a second
// route to the same content is worth trying only after the first said no.
const served = async (
  primary: () => Promise<StandardResponse>,
  fallback?: () => Promise<StandardResponse>
): Promise<StandardResponse> =>
  fallback
    ? ResultResolve<StandardResponse | Promise<StandardResponse>, AnyError>(
        await SafePromise(primary()),
        fallback
      )
    : primary()

// Lazy files — a merge, a zip, a plot — have no bytes until somebody asks:
// the plan is the primary route and usually a cache hit. `content`, when a
// storage attached one, is already-materialized bytes to fall back on for a
// plan that cannot produce here.
//
// This and {@link single} are the two orderings over the same machinery, and
// the order is the whole distinction: here the plan is asked first, there the
// content is. One function with a flag to pick the order would hide the only
// thing worth seeing.
const lazy = (
  file: PlannedFile,
  engine: Engine,
  options: PdfEngineOptions,
  emit: Emit
): Promise<StandardResponse> =>
  served(
    produced(file, engine, options, emit),
    file.content && Partial(dispatch, file.content, engine, options, emit)
  )

// Content-bearing files — a split page, an image — carry their bytes: those
// first, and the plan (cut or rasterize the source again) when they expired.
// The reverse of {@link lazy}'s order, for the reason given there.
const single = (
  file: PlannedFile & { content: RemoteFile },
  engine: Engine,
  options: PdfEngineOptions,
  emit: Emit
): Promise<StandardResponse> =>
  served(
    Partial(dispatch, file.content, engine, options, emit),
    produced(file, engine, options, emit)
  )

const dispatch: (
  file: VirtualFile,
  engine: Engine,
  options: PdfEngineOptions,
  emit: Emit
) => Promise<StandardResponse> = FullMethodDispatcher<
  [VirtualFile, Engine, PdfEngineOptions, Emit],
  Promise<StandardResponse>
>({
  // Anything named by a url — a download, and equally whatever this engine
  // generated, which is a url into its own file cache. `info` is what tells
  // the two apart: a file that already knows its own size, etag and write time
  // has been written somewhere, and its mirrors are then read for the bytes
  // alone, nothing about them asked twice. Without it the read is also how the
  // engine finds out, and what the response declares is all there is to go on.
  [VirtualFileType.REMOTE_FILE]: async (file, _engine, _options, emit) => {
    const response = await try_mirrors(file, emit)

    // No mirror served it. Bytes this engine wrote and lost are recoverable —
    // callers holding the producing plan intercept CACHE_EXPIRED and
    // re-execute with cache_evict — but a url nobody else can answer is gone.
    if (!response) throw EngineError(file.info ? ErrorName.CACHE_EXPIRED : ErrorName.FILE_NOT_FOUND)

    // How long the bytes are and when they landed are whoever holds them to
    // say. What they *are* is not: content named by its hash has no extension
    // to guess a type from, and a server's content-type describes a download
    // rather than a document — the file is the only one here that knows, and
    // its guid is the name it is known by however the response spells it.
    return RecordAssign(response, {
      info: { ...(file.info ?? response.info), etag: file.guid, type: file.type },
    })
  },

  // A merged file goes out behind its integrity line (see PDFMergeIntegrity):
  // that first, then the merge as it was stored. A reader trusts the merge's
  // own layout only while the file is still the size it records.
  [VirtualFileType.MULTI_PAGE_PDF]: async (file, engine, options, emit) => {
    const { body, info } = await lazy(file, engine, options, emit)
    const integrity = PDFMergeIntegrity(info.size)
    return ResponseCreate(StreamCreate(StreamToIterable([integrity, body])), {
      ...info,
      size: (info.size + integrity.length) as Bytes,
    })
  },
  [VirtualFileType.ZIP_FILE]: lazy,
  [VirtualFileType.SINGLE_PAGE_IMAGE]: single,
  [VirtualFileType.BINARY_PAGE_PDF]: single,
  [VirtualFileType.PLOTTED_PAGE_PDF]: lazy,
})

/** Any VirtualFile as the response that serves it — produced on demand where
 * the file is a recipe rather than content (see FilePlan).
 *
 * The body stays a stream: a merge of a hundred pages reads each page as it
 * comes, and a file too big to hold is read in pieces by whoever can (see
 * ResizeUInt8). `info` is everything the engine knows about the bytes: its
 * etag is the file's guid, its type the file's own, whatever the serving
 * response's headers say. */
export const RequestFile = (
  engine: Engine,
  options: PdfEngineOptions,
  file: VirtualFile,
  emit: Emit = Void
): Promise<StandardResponse> => dispatch(file, engine, options, emit)
